/**
 * DrillPlanLibraryPage — global drill-plan library (G3).
 *
 * Lists all globally readable drill plans with tag + name search.
 * Practitioners can link / assign an existing plan to their selected client.
 *
 * @see docs/features/260331-automated-driller-mvp/FEATURE_SPEC.md G3, G2
 */

import React, { useState, useEffect, useMemo } from 'react';
import { Search, Plus, Tag, UserCheck, Calendar, Layers } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import PageLayout from '@/components/shared/PageLayout';
import { useClient } from '@/context/client-context';
import { listDrillPlans } from './services/drill-plans-api';
import type { DrillPlan } from './types/drill-plan-types';
import AssignPlanDialog from './AssignPlanDialog';
import { useToast } from '@/hooks/use-toast';
import logger from '@/lib/logging';

const DrillPlanLibraryPage: React.FC = () => {
  const { client } = useClient();
  const { toast } = useToast();

  const [plans, setPlans] = useState<DrillPlan[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPlan, setSelectedPlan] = useState<DrillPlan | null>(null);
  const [isAssignOpen, setIsAssignOpen] = useState(false);

  const fetchPlans = async (q?: string) => {
    setIsLoading(true);
    try {
      const rows = await listDrillPlans(q || undefined);
      setPlans(rows);
    } catch (err) {
      logger.error('drill-plan-library:fetch:error', err);
      toast({
        title: 'Failed to load drill plans',
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchPlans();
  }, []);

  // Client-side filter on top of server search (for instant feedback)
  const filtered = useMemo(() => {
    if (!searchQuery.trim()) return plans;
    const q = searchQuery.toLowerCase();
    return plans.filter(
      (p) =>
        p.name?.toLowerCase().includes(q) ||
        (Array.isArray(p.tags) && p.tags.some((t) => String(t).toLowerCase().includes(q)))
    );
  }, [plans, searchQuery]);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  const handleAssign = (plan: DrillPlan) => {
    if (!client) {
      toast({
        title: 'No client selected',
        description: 'Select a client first to assign a drill plan.',
        variant: 'destructive',
      });
      return;
    }
    setSelectedPlan(plan);
    setIsAssignOpen(true);
  };

  return (
    <PageLayout
      title="Drill Plan Library"
      subtitle="Browse and assign globally available drill plans"
      titleBadge="Beta"
      headerActions={
        <Button asChild size="sm" className="gap-2 h-10">
          <Link to="/drill-planner?mode=create">
            <Plus className="h-4 w-4" />
            <span className="hidden sm:inline">New Plan</span>
          </Link>
        </Button>
      }
    >
      <div className="space-y-4">
        {/* Search bar */}
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by name or tag…"
            value={searchQuery}
            onChange={handleSearch}
            className="pl-9"
          />
        </div>

        {/* Plan list */}
        {isLoading ? (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {[1, 2, 3, 4].map((i) => (
              <Skeleton key={i} className="h-40 rounded-lg" />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <Layers className="h-10 w-10 mx-auto mb-3 opacity-30" />
            <p className="text-sm">
              {plans.length === 0
                ? 'No drill plans yet. Create the first one!'
                : 'No plans match your search.'}
            </p>
          </div>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {filtered.map((plan) => (
              <PlanCard
                key={plan.id}
                plan={plan}
                onAssign={handleAssign}
                canAssign={!!client}
              />
            ))}
          </div>
        )}
      </div>

      {/* Assign dialog */}
      <AssignPlanDialog
        plan={selectedPlan}
        isOpen={isAssignOpen}
        onClose={() => {
          setIsAssignOpen(false);
          setSelectedPlan(null);
        }}
        onAssigned={() => fetchPlans(searchQuery || undefined)}
      />
    </PageLayout>
  );
};

interface PlanCardProps {
  plan: DrillPlan;
  onAssign: (plan: DrillPlan) => void;
  canAssign: boolean;
}

const PlanCard: React.FC<PlanCardProps> = ({ plan, onAssign, canAssign }) => {
  const dayCount = Array.isArray(plan.days) ? plan.days.length : 0;
  const slotCount = dayCount > 0 ? Object.keys(plan.days[0]?.slots ?? {}).length : 0;

  return (
    <Card className="flex flex-col">
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between gap-2">
          <CardTitle className="text-sm font-semibold leading-snug">{plan.name}</CardTitle>
          {plan.phase && (
            <Badge variant="secondary" className="text-xs flex-shrink-0">
              {plan.phase}
            </Badge>
          )}
        </div>
        {plan.description && (
          <p className="text-xs text-muted-foreground line-clamp-2">{plan.description}</p>
        )}
      </CardHeader>

      <CardContent className="flex flex-col gap-3 flex-1">
        {/* Meta */}
        <div className="flex items-center gap-3 text-xs text-muted-foreground">
          <span className="flex items-center gap-1">
            <Calendar className="h-3.5 w-3.5" />
            {dayCount} day{dayCount !== 1 ? 's' : ''}
          </span>
          <span className="flex items-center gap-1">
            <Layers className="h-3.5 w-3.5" />
            {slotCount} slot{slotCount !== 1 ? 's' : ''}/day
          </span>
          {plan.twiceDaily && <Badge variant="outline" className="text-xs">2×/day</Badge>}
        </div>

        {/* Tags */}
        {Array.isArray(plan.tags) && plan.tags.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {plan.tags.slice(0, 5).map((tag) => (
              <span
                key={tag}
                className="inline-flex items-center gap-1 text-xs bg-muted text-muted-foreground rounded px-1.5 py-0.5"
              >
                <Tag className="h-3 w-3" />
                {tag}
              </span>
            ))}
            {plan.tags.length > 5 && (
              <span className="text-xs text-muted-foreground">+{plan.tags.length - 5}</span>
            )}
          </div>
        )}

        {/* Assign button */}
        <div className="mt-auto pt-2">
          <Button
            variant="outline"
            size="sm"
            className="w-full gap-2"
            onClick={() => onAssign(plan)}
            disabled={!canAssign}
            title={!canAssign ? 'Select a client first' : `Assign to selected client`}
          >
            <UserCheck className="h-3.5 w-3.5" />
            Link to Client
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default DrillPlanLibraryPage;
